import { useCallback, useRef, useState } from 'react';

import { useFormik } from 'formik';
import { useIntl } from 'react-intl';
import { date, object, string } from 'yup';

import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import Grid from '@mui/material/Grid2';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

import Dropdown from 'src/components/Dropdown';
import ConfirmModal from 'src/components/ConfirmModal';
import { useCancelContract } from 'src/pages/ContractsPage/api';
import { DatePickerField } from 'src/components/DatePickerField';

import type { Moment } from 'moment';

import messages from './messages';

interface CancelLongTermContractFormProps {
  contractUuid: string;
}

export default function CancelLongTermContractForm({
  contractUuid,
}: CancelLongTermContractFormProps) {
  const { formatMessage } = useIntl();

  const [validateOnChange, setValidateOnChange] = useState(false);

  const { mutate: cancelContract, isPending: isCancellingContract } =
    useCancelContract();

  const cancellationSourceOptions = useRef([
    {
      value: 'ucm',
      label: formatMessage(messages.cancelByUcmLabel),
    },
    {
      value: 'candidate',
      label: formatMessage(messages.cancelByCandidateLabel),
    },
  ]).current;

  const formik = useFormik({
    initialValues: {
      endDate: null,
      requestedDate: null,
      cancellationSource: '',
    },
    validateOnChange: validateOnChange,
    validationSchema: object().shape({
      endDate: date().required(),
      requestedDate: date().required(),
      cancellationSource: string().required(),
    }),
    onSubmit: () => {},
  });

  const { values, errors } = formik;

  const handleClose = useCallback(() => {
    setValidateOnChange(false);
    formik.resetForm();
  }, []);

  const handleDateChange = useCallback(
    (name: string, value: Moment | Array<Moment> | null) => {
      if (!isArray(value)) formik.setFieldValue(name, value);
      return Promise.resolve();
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    setValidateOnChange(true);

    const errors = await formik.validateForm();
    const isValid = isEmpty(errors);

    if (!isValid) return false;

    return new Promise<boolean>((resolve) => {
      cancelContract(
        { ...values, contractUuid },
        {
          onSuccess: () => {
            resolve(true);
            handleClose();
          },
          onError: () => {
            resolve(false);
          },
        },
      );
    });
  }, [values, contractUuid]);

  return (
    <ConfirmModal
      onClose={handleClose}
      onConfirm={handleSubmit}
      loading={isCancellingContract}
      title={formatMessage(messages.title)}
      submitTextButton={formatMessage(messages.confirmButtonLabel)}
      triggerButton={(handleOpen) => (
        <Tooltip arrow title={formatMessage(messages.cancelTooltip)}>
          <IconButton onClick={handleOpen}>
            <CloseOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    >
      <Grid container spacing={2}>
        <Grid size={12}>
          <Dropdown
            fullWidth
            name="cancellationSource"
            onChange={formik.handleChange}
            value={values.cancellationSource}
            options={cancellationSourceOptions}
            error={!!errors.cancellationSource}
            helperText={errors.cancellationSource}
            label={formatMessage(messages.cancellationSourceLabel)}
          />
        </Grid>

        <Grid size={12}>
          <DatePickerField
            allowPicker
            allowTyping
            name="requestedDate"
            value={values.requestedDate}
            error={!!errors.requestedDate}
            helperText={errors.requestedDate}
            label={formatMessage(messages.requestedDateLabel)}
            onBlurChange={(date) => handleDateChange('requestedDate', date)}
            onChange={(date) => handleDateChange('requestedDate', date)}
          />
        </Grid>

        <Grid size={12}>
          <DatePickerField
            allowPicker
            allowTyping
            name="endDate"
            value={values.endDate}
            error={!!errors.endDate}
            helperText={errors.endDate}
            label={formatMessage(messages.noticeEndDateLabel)}
            onBlurChange={(date) => handleDateChange('endDate', date)}
            onChange={(date) => handleDateChange('endDate', date)}
          />
        </Grid>
      </Grid>
    </ConfirmModal>
  );
}
